import {OperatingSystem} from './operating-system';
import {AttributeGroup, OsComponent} from './os-component';

export interface Product {
  parentId: number;
  parentSmartNumber: string;
  id: number;
  smartNumber: string;
  name: string;
  version: string;
  url: null | string;
  urlTitle: null | string;
  productStatus: string;
  parentProductType: string;
  productType: string;
  availabityDate: string;
  fullSuppEndDate: null | string;
  projObsDate: null | string;
  servicePackName: null | string;
  servicePackAvailabilityDate: null | string;
  spSmartNumber2Name: {[smartNumber: string]: string};
  servicePacks?: ProductServicePack[];
  masterProductCount: number;
  childProductsAvailable: boolean;
  osAvailable: boolean;
  hasPublishedVersion: boolean;
  hasFeedback: boolean;
  supportStatus: SUPPORT_STATUS;
  originalProductSmartNumber: string;
}

interface LastModifiedInfo {
  action: string;
  id: number;
  itemType: PRODUCT_TYPE.PRODUCT | PRODUCT_TYPE.SOLUTION | PRODUCT_TYPE.SUITE;
  modificationDate: string;
  modifiedItemId: number;
  user: {
    id: number,
    username: string
  };
}

export enum SUPPORT_STATUS {
  LIMITED_SUPPORT = 'Limited Support',
  END_OF_VERSION_SUPPORT = 'End of Version Support',
  FULL_SUPPORT = 'Full Support'
}

export interface ProductDto {
  url?: string;
  urlTitle?: string;
  productType?: PRODUCT_TYPE.PRODUCT | PRODUCT_TYPE.SUITE;
}

export interface ProductInfo extends Product {
  publishingStatus: PublishingStatus.NEW |
    PublishingStatus.DRAFT |
    PublishingStatus.REVIEW |
    PublishingStatus.PUBLISHED |
    PublishingStatus.WITHDRAWN;
  lastModifiedInfo: LastModifiedInfo;
}

export interface ProductInfoExpanded extends ProductInfo {
  bmcComponentsCount: number;
  compatibleBmcProductsCount: number;
  compatiblePlatformsCount: number;
  coreComponentsCount: number;
  notesAndInformationCount: number;
  prerequisiteBmcProductsCount: number;
  suiteOrSolutionCount: number;
}

// TODO remove SERVICE_PACK from PRODUCT_TYPE
export enum PRODUCT_TYPE {
  SUITE = 'SUITE',
  SOLUTION = 'SOLUTION',
  PRODUCT = 'PRODUCT',
  COMPONENT = 'COMPONENT',
  SERVICE_PACK = 'SERVICE_PACK'
}

export class Language {
  constructor(
    public id: number,
    public name: string,
  ) {
    this.id = id;
    this.name = name;
  }
}

export interface ProductDetail {
  prodInstId: number;
  operatingSystemDto: OperatingSystem;
  attributeGroups: AttributeGroup[];
  components: OsComponent[];
  uiLanguages: Language[];
  docLanguages: Language[];
}

export enum ProductRelations {
  COMPATIBLE_BMC_PRODUCTS = 'COMPATIBLE_BMC_PRODUCTS',
  PREREQUISITE_BMC_PRODUCTS = 'PREREQUISITE_BMC_PRODUCTS',
  CORE_COMPONENTS = 'CORE_COMPONENTS',
  COMPONENTS = 'COMPONENTS',
  SUITE_OR_SOLUTION = 'SUITE_OR_SOLUTION'
}

export interface ProductNote {
  content: string;
  id: number;
  title: string;
  sequenceNumber: number;
}

export interface ProductServicePack {
  smartNumber: string;
  name: string;
}

export enum PublishingStatusText {
  NEW = 'New',
  DRAFT = 'Draft',
  REVIEW = 'Review',
  PUBLISHED = 'Published',
  WITHDRAWN = 'Withdrawn'
}

export enum PublishingStatus {
  NEW = 'NEW',
  DRAFT = 'DRAFT',
  REVIEW = 'REVIEW',
  PUBLISHED = 'PUBLISHED',
  WITHDRAWN = 'WITHDRAWN'
}

export enum ProductStatus {
  SUPPORT_ONLY = 'SUPPORT_ONLY',
  WIP = 'WIP',
  AVAILABLE = 'AVAILABLE',
  OBSOLETE = 'OBSOLETE',
  WITHDRAWN = 'WITHDRAWN',
  GA = 'GA',
  CANCELED = 'CANCELED',
  DEVELOPMENT = 'DEVELOPMENT',
  BETA = 'BETA',
  PLANNING = 'PLANNING',
  TRANSITION = 'TRANSITION'
}

export enum PRODUCT_SUB_TYPE {
  DM___Developed_Module = 'DM___Developed_Module',
  DR___3PCOMMERCIAL = 'DR___3PCOMMERCIAL',
  DR___3PMKTRESALE = 'DR___3PMKTRESALE',
  DR___3POEM_ROYALTY = 'DR___3POEM_ROYALTY',
  DR___3POPENSOURCE = 'DR___3POPENSOURCE',
  DR___3PROYALFREETE = 'DR___3PROYALFREETE',
  DR___3PRLTY_STDALONE = 'DR___3PRLTY_STDALONE',
  DR___3PROYALTEMBED = 'DR___3PROYALTEMBED',
  DR___3PTECHDISABLED = 'DR___3PTECHDISABLED',
  DR___Developed_Record = 'DR___Developed_Record',
  DR___Internal_Project = 'DR___Internal_Project',
  DR___MTU = 'DR___MTU',
  DR___Web_Display_Developed_Rec = 'DR___Web_Display_Developed_Rec',
  HD___Hardware = 'HD___Hardware',
  ED___Education = 'ED___Education',
  LA___Development_License = 'LA___Development_License',
  LA___Hot_Backup_License = 'LA___Hot_Backup_License',
  LA___Licensed_Add_On = 'LA___Licensed_Add_On',
  LP___Beta_Product = 'LP___Beta_Product',
  LP___Licensed_Product = 'LP___Licensed_Product',
  LP___PSEUDO_MKT = 'LP___PSEUDO_MKT',
  LP___PSEUDO_DEV = 'LP___PSEUDO_DEV',
  LY___Licensed_3rd_Party = 'LY___Licensed_3rd_Party',
  PM___Promo_Package = 'PM___Promo_Package',
  PP___Integrated_Package = 'PP___Integrated_Package',
  PP___Non_Integrated_Package = 'PP___Non_Integrated_Package',
  PS___Professional_Services = 'PS___Professional_Services',
  RY_Resale_of_3rd_Party_Product = 'RY_Resale_of_3rd_Party_Product',
  RO___Membership_Fees = 'RO___Membership_Fees',
  RO___Others = 'RO___Others',
  SP___Service_Product = 'SP___Service_Product',
}

export const BadgeClasses = {
  [PublishingStatus.NEW]: 'badge-danger',
  [PublishingStatus.DRAFT]: 'badge-warning',
  [PublishingStatus.REVIEW]: 'badge-info',
  [PublishingStatus.PUBLISHED]: 'badge-success',
  [PublishingStatus.WITHDRAWN]: 'badge-dark',
  [ProductStatus.AVAILABLE]: 'badge-success',
  [ProductStatus.SUPPORT_ONLY]: 'badge-success',
  [ProductStatus.TRANSITION]: 'badge-success',
  [ProductStatus.OBSOLETE]: 'badge-dark',
  [ProductStatus.WIP]: 'badge-dark',
};
